import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { fetchPricing, setCoupon, clearCoupon } from '../../store/pricingSlice'
import React, { useState, useEffect, useRef } from 'react'
import messages from './messages'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import DiscountIcon from 'media/discount.svg?svgr-webpack'
import { Link } from 'react-router-dom'
import GiftIcon from 'media/gift.svg?svgr-webpack'
import useBreakpoints from '../../hooks/useBreakpoints'
import Star from 'media/star.svg?svgr-webpack'

const UNDEFINED = 0
const VALID = 1
const EXPIRED = 2
const NOT_FOUND = 3

function Form({ onSubmit, currentCoupon }) {
  const fieldRef = useRef()
  const [typedCoupon, setTypedCoupon] = useState(currentCoupon || '')
  const { formatMessage } = useIntl()

  useEffect(() => {
    fieldRef.current && fieldRef.current.focus()
  }, [])

  return (
    <form
      className="relative mx-auto inline-block"
      onSubmit={(e) => onSubmit(e, typedCoupon)}
    >
      <label className="sr-only" htmlFor="type-coupon">
        <FormattedMessage
          defaultMessage="Discount code"
          id="manage_coupon_label"
        />
      </label>
      <input
        ref={fieldRef}
        placeholder={formatMessage({
          id: 'manage_coupon_enter',
          defaultMessage: 'Enter code',
        })}
        type="text"
        style={{ width: 300 }}
        id="type-coupon"
        className={cn(
          'text-small',
          'pb-6 pl-48 pr-16 pt-8',
          'rounded-6',
          'border-1 border-grey-lightest focus:border-grey-lighter',
        )}
        value={typedCoupon}
        onChange={(e) => setTypedCoupon(e.target.value)}
      />
      <div className="absolute left-0 top-0 ml-12 mt-4 text-green">
        <DiscountIcon style={{ height: 21 }} />
      </div>
      <button
        disabled={
          currentCoupon &&
          typedCoupon.toLowerCase() === currentCoupon.toLowerCase()
        }
        className={cn(
          'type-upper-fat',
          'absolute right-0 top-0',
          'px-16 py-8',
          currentCoupon &&
            typedCoupon.toLowerCase() === currentCoupon.toLowerCase()
            ? 'sr-only'
            : 'text-green',
        )}
        type="submit"
      >
        <FormattedMessage defaultMessage="OK" id="manage_coupon_button" />
      </button>
    </form>
  )
}

Form.propTypes = {
  onSubmit: PropTypes.func,
  currentCoupon: PropTypes.string,
}

function Badge({ coupon, valid = true, hideCoupon }) {
  return (
    <h2
      className={cn(
        'inline-flex justify-center',
        'px-12',
        'rounded-full',
        valid
          ? 'border-1 border-green'
          : 'absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform bg-secondary-red text-white',
      )}
    >
      <div className={cn({ 'text-green': valid, 'mr-8': !hideCoupon })}>
        <DiscountIcon style={{ height: 21 }} />
      </div>
      {!hideCoupon && <div className="type-upper mt-4">{coupon}</div>}
    </h2>
  )
}

Badge.propTypes = {
  valid: PropTypes.bool,
  coupon: PropTypes.string,
  hideCoupon: PropTypes.bool,
}

export default function ManageCoupon({
  editable = true,
  coupon,
  hideCoupon,
  pricingConfig,
  onChange,
}) {
  const dispatch = useDispatch()
  const { MD } = useBreakpoints()
  const [currentCoupon, setCurrentCoupon] = useState(
    coupon || pricingConfig.coupon,
  )
  const [couponState, setCouponState] = useState(
    pricingConfig.coupon ? VALID : UNDEFINED,
  )
  const [errorCount, setErrorCount] = useState(0)
  const [isExpanded, setIsExpanded] = useState(currentCoupon)
  const [showValidForm, setShowValidForm] = useState(false)

  useEffect(() => {
    if (!currentCoupon || !isExpanded || currentCoupon === pricingConfig.coupon)
      return

    dispatch(fetchPricing(currentCoupon))
      .then(() => {
        dispatch(setCoupon(currentCoupon))
        setCouponState(VALID)
        setShowValidForm(false)
      })
      .catch((err) => {
        dispatch(clearCoupon())
        if (err.errorDetail && err.errorDetail.indexOf('expire') > -1) {
          setCouponState(EXPIRED)
        } else {
          setCouponState(NOT_FOUND)
          setErrorCount(errorCount + 1)
        }
      })
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCoupon])

  useEffect(() => {
    if (onChange) onChange(pricingConfig.discount)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingConfig.discount])

  const loadNewCoupon = (e, coupon) => {
    e.preventDefault()
    setCouponState(UNDEFINED)
    setCurrentCoupon(coupon)
  }

  const continueMessage = (
    <FormattedMessage
      id="SignUp.continueToClaim"
      defaultMessage="Continue to claim your <b>{percentage}</b> discount"
      values={{
        b: function BoldText(...chunks) {
          return <b className="font-demibold">{chunks}</b>
        },
        percentage:
          pricingConfig.discountType === '%' ? (
            <FormattedNumber
              value={pricingConfig.discount / 100}
              style={'percent'}
            />
          ) : (
            <FormattedNumber
              value={pricingConfig.discount}
              currency={pricingConfig.currency}
              style={'currency'}
              maximumFractionDigits={2}
            />
          ),
      }}
    />
  )

  const trialHeading = (
    <>
      <div className="flex justify-center px-16 pt-6">
        <Star className="mr-4 lg:mr-6" />
        <p className="type-14-demibold-caps lg:type-17-demibold-caps">
          <FormattedMessage
            id="trial_tryDaysForFree"
            defaultMessage="Try {days} days for free"
            values={{ days: pricingConfig.trialDuration }}
          />
        </p>
      </div>
    </>
  )

  const isTrial = pricingConfig.trialDuration !== 0
  const consolationCode = couponState === EXPIRED ? 'solace20' : 'reward20'

  return (
    <div className="type-small text-center">
      {!editable && couponState === VALID && (
        <>
          {isTrial ? (
            trialHeading
          ) : (
            <>
              <Badge coupon={currentCoupon} hideCoupon={hideCoupon} />
              <p className="type-nano mt-8">{continueMessage}</p>
            </>
          )}
        </>
      )}
      {editable && (
        <>
          {!isExpanded ? (
            <div className={cn(MD ? 'text-left' : 'mb-12')}>
              <button
                className={cn(
                  'type-nano',
                  'relative z-10',
                  'text-blue-dark hover:text-secondary-blue',
                  'cursor-pointer transition',
                )}
                onClick={() => setIsExpanded(true)}
              >
                <DiscountIcon
                  style={{ height: 21 }}
                  className="mr-4 translate-y-1 transform"
                />
                <FormattedMessage
                  id="manage_coupon_heading"
                  defaultMessage="Have a discount code?"
                />
              </button>
            </div>
          ) : (
            <>
              {currentCoupon && couponState === UNDEFINED ? (
                <img src={require('media/spinner.gif')} alt="Loading coupon…" />
              ) : (
                <>
                  {(couponState === EXPIRED || couponState === NOT_FOUND) && (
                    <div
                      className={cn(
                        'relative',
                        'mb-24 mt-12',
                        'px-20 pb-12 pt-20',
                        'border-1 border-secondary-red',
                        'rounded-8',
                      )}
                    >
                      <Badge valid={false} coupon={currentCoupon} />
                      <p className="type-small mt-12" data-uid="error_message">
                        {couponState === EXPIRED ? (
                          <FormattedMessage {...messages.expiredMessage} />
                        ) : (
                          <FormattedMessage {...messages.invalidMessage} />
                        )}
                      </p>
                      {(couponState === EXPIRED || errorCount > 1) && (
                        <p className="type-small mt-12">
                          {couponState === EXPIRED ? (
                            <FormattedMessage
                              id="manage_coupon_expired_consolation"
                              defaultMessage="We’d like to offer you a consolation discount. Use this code for {discount}% off:"
                              values={{ discount: '20' }}
                            />
                          ) : (
                            <FormattedMessage
                              id="manage_coupon_deal_hunter"
                              defaultMessage="We see that you are persistent so we’d like to reward you with a {discount} off code:"
                              values={{ discount: '20%' }}
                            />
                          )}{' '}
                          <button
                            className="cursor-pointer text-blue-dark transition hover:text-secondary-blue"
                            onClick={(e) => loadNewCoupon(e, consolationCode)}
                          >
                            <DiscountIcon
                              style={{ height: 21 }}
                              className="mr-4 translate-y-1 transform"
                            />
                            {consolationCode}
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                  {couponState === VALID ? (
                    <>
                      {isTrial ? (
                        trialHeading
                      ) : (
                        <>
                          <Badge coupon={currentCoupon} />
                          <p className="type-nano mt-8">{continueMessage}</p>
                        </>
                      )}
                      {showValidForm ? (
                        <div className="mt-8">
                          <Form
                            onSubmit={loadNewCoupon}
                            currentCoupon={currentCoupon}
                          />
                        </div>
                      ) : (
                        <button
                          className={cn(
                            'type-nano font-demibold',
                            'text-blue-dark hover:text-secondary-blue',
                            'cursor-pointer transition',
                          )}
                          onClick={() => setShowValidForm(true)}
                        >
                          <FormattedMessage
                            id="manage_coupon_change"
                            defaultMessage="Change code"
                          />
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Form
                        onSubmit={loadNewCoupon}
                        currentCoupon={currentCoupon}
                      />
                      <p className="type-nano mt-12">
                        <span
                          className={cn(
                            'text-grey-darker',
                            'mr-6',
                            'inline-block',
                            'relative',
                          )}
                          style={{ top: 2 }}
                        >
                          <GiftIcon className="fill-current" />
                        </span>
                        <FormattedMessage
                          id="manage_coupon_gift_card"
                          defaultMessage="Have a gift card? <a>Activate here</a>."
                          values={{
                            a: function GiftCardLink(...chunks) {
                              return (
                                <Link
                                  className={cn(
                                    'transition',
                                    'font-demibold',
                                    'text-blue-dark hover:text-secondary-blue',
                                  )}
                                  to="/voucher"
                                >
                                  {chunks}
                                </Link>
                              )
                            },
                          }}
                        />
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

ManageCoupon.propTypes = {
  coupon: PropTypes.string,
  hideCoupon: PropTypes.bool,
  pricingConfig: PropTypes.shape({
    discount: PropTypes.number,
    discountType: PropTypes.string,
    coupon: PropTypes.string,
    currency: PropTypes.string,
    trialDuration: PropTypes.number,
  }),
  editable: PropTypes.bool,
  onChange: PropTypes.func,
}
